import type { components } from "../../shared/api/generated/trading-engine.js";
import {
  backtestChartView,
  tickerBarsView,
  type BacktestChartView,
  type TickerBarsView,
} from "./chart-models.js";

export interface ChartPeriod {
  readonly from: string;
  readonly to: string;
}
export interface PeriodProgress {
  readonly requests: number;
  readonly through: string;
}
type EvidencePage = components["schemas"]["BacktestChart"];
type BarPage = components["schemas"]["TickerBars"];
export const MARKET_WINDOW_MS = 7 * 86_400_000;
// Basic Alpaca SIP access excludes the latest 15 minutes, even after hours.
// Keep a one-minute buffer for request timing and clock differences.
export const LIVE_MARKET_DELAY_MS = 16 * 60_000;

function validPeriod(period: ChartPeriod): void {
  if (
    !Number.isFinite(Date.parse(period.from)) ||
    !Number.isFinite(Date.parse(period.to)) ||
    Date.parse(period.from) >= Date.parse(period.to)
  ) {
    throw new Error("The chart period is invalid.");
  }
}

/** Stitch exact bounded responses, including empty pages, without exposing pagination to the user. */
export async function loadBacktestPeriod(
  runId: string,
  period: ChartPeriod,
  read: (cursor?: string) => Promise<EvidencePage>,
  signal: AbortSignal,
  onProgress: (progress: PeriodProgress) => void,
): Promise<BacktestChartView> {
  validPeriod(period);
  let cursor: string | undefined;
  let first: EvidencePage | undefined;
  const bars: EvidencePage["bars"] = [];
  const fills: EvidencePage["fills"] = [];
  const decisions: EvidencePage["decisions"] = [];
  const equityCurve: EvidencePage["equityCurve"] = [];
  let requests = 0;
  do {
    signal.throwIfAborted();
    const page = await read(cursor);
    signal.throwIfAborted();
    if (
      page.runId !== runId ||
      page.range === undefined ||
      Date.parse(page.range.from) !== Date.parse(cursor ?? period.from) ||
      Date.parse(page.range.to) < Date.parse(page.range.from) ||
      Date.parse(page.range.to) > Date.parse(period.to) ||
      !Number.isFinite(Date.parse(page.range.to))
    ) {
      throw new Error("Backtest evidence does not cover the requested period contiguously.");
    }
    if (
      first !== undefined &&
      (page.symbol !== first.symbol ||
        page.botRunId !== first.botRunId ||
        page.interval !== first.interval ||
        page.barSource !== first.barSource ||
        JSON.stringify(page.versions) !== JSON.stringify(first.versions) ||
        page.decisionScope !== first.decisionScope)
    ) {
      throw new Error("Backtest evidence provenance changed between requests.");
    }
    const next = page.nextCursor;
    if (
      (next !== undefined && Date.parse(next) !== Date.parse(page.range.to) + 1) ||
      (next === undefined && Date.parse(page.range.to) !== Date.parse(period.to))
    ) {
      throw new Error("Backtest evidence pagination is incomplete or does not advance.");
    }
    const pageFrom = Date.parse(page.range.from);
    const pageTo = Date.parse(page.range.to);
    const inRange = (instant: string) =>
      Date.parse(instant) >= pageFrom && Date.parse(instant) <= pageTo;
    if (
      page.bars.some((bar) => !inRange(bar.endedAt)) ||
      page.fills.some((fill) => !inRange(fill.occurredAt)) ||
      page.decisions.some((decision) => !inRange(decision.barEndedAt)) ||
      page.equityCurve.some((point) => !inRange(point.at))
    ) {
      throw new Error("Backtest evidence contains events outside its coverage range.");
    }
    first ??= page;
    // Iteration avoids exceeding the engine's argument limit on large arrays.
    for (const bar of page.bars) bars.push(bar);
    for (const fill of page.fills) fills.push(fill);
    for (const decision of page.decisions) decisions.push(decision);
    for (const point of page.equityCurve) equityCurve.push(point);
    onProgress({ requests: ++requests, through: page.range.to });
    cursor = next;
  } while (cursor !== undefined);
  if (first === undefined) throw new Error("Backtest evidence was not returned.");
  const result = backtestChartView({
    ...first,
    range: period,
    bars,
    fills,
    decisions,
    equityCurve,
  });
  // A fully assembled chart has no continuation cursor.
  const complete = { ...result };
  delete complete.nextCursor;
  return complete;
}

/** Short requests stay below API range/response limits. Shared boundary bars are deduplicated. */
export async function loadMarketPeriod(
  symbol: string,
  interval: string,
  period: ChartPeriod,
  read: (range: ChartPeriod, historical: boolean) => Promise<BarPage>,
  signal: AbortSignal,
  onProgress: (progress: PeriodProgress) => void,
): Promise<TickerBarsView> {
  validPeriod(period);
  const bars = new Map<string, BarPage["bars"][number]>();
  let start = Date.parse(period.from);
  const end = Date.parse(period.to);
  let dataVersion: string | undefined;
  let requests = 0;
  while (start < end) {
    signal.throwIfAborted();
    const to = Math.min(end, start + MARKET_WINDOW_MS);
    const range = { from: new Date(start).toISOString(), to: new Date(to).toISOString() };
    const page = await read(range, to < end);
    signal.throwIfAborted();
    if (
      page.symbol !== symbol ||
      page.interval !== interval ||
      page.adjustment !== "raw" ||
      Date.parse(page.from) !== start ||
      Date.parse(page.to) !== to ||
      (dataVersion !== undefined && page.dataVersion !== dataVersion)
    ) {
      throw new Error("Live chart data does not match the requested symbol, interval, or period.");
    }
    dataVersion = page.dataVersion;
    for (const bar of page.bars) {
      if (Date.parse(bar.startedAt) >= Date.parse(period.from) && Date.parse(bar.startedAt) <= end)
        bars.set(bar.startedAt, bar);
    }
    onProgress({ requests: ++requests, through: range.to });
    start = to;
  }
  return tickerBarsView({
    symbol,
    interval: interval as BarPage["interval"],
    adjustment: "raw",
    ...period,
    dataVersion: dataVersion ?? "",
    bars: [...bars.values()].sort((a, b) => a.startedAt.localeCompare(b.startedAt)),
  });
}

/** Covers recorded fill history through the free Alpaca SIP cutoff. */
export function liveChartPeriod(
  executions: readonly { readonly occurredAt: string }[],
  interval: string,
  now = new Date(),
): ChartPeriod {
  const cutoff = now.getTime() - LIVE_MARKET_DELAY_MS;
  const earliest = executions.reduce(
    (value, row) =>
      Number.isFinite(Date.parse(row.occurredAt))
        ? Math.min(value, Date.parse(row.occurredAt))
        : value,
    cutoff,
  );
  const from = new Date(
    executions.length === 0 ? now.getTime() - (interval === "1d" ? 30 : 3) * 86_400_000 : earliest,
  );
  from.setUTCHours(0, 0, 0, 0);
  return { from: from.toISOString(), to: new Date(cutoff).toISOString() };
}
